import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Toolbar from '@mui/material/Toolbar';

import { matchPath, useLocation, useHistory } from 'react-router';
import DashboardSidebarContext from './DashboardSidebarContext';
import ServiceContext from './ServiceContext';
import { SERVICE_COLORS } from './theme';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from './constants';
import DashboardSidebarPageItem from './DashboardSidebarPageItem';
import DashboardSidebarHeaderItem from './DashboardSidebarHeaderItem';
import DashboardSidebarDividerItem from './DashboardSidebarDividerItem';
import {
  getDrawerSxTransitionMixin,
  getDrawerWidthTransitionMixin,
} from './mixins';

import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PermMediaRoundedIcon from '@mui/icons-material/PermMediaRounded';
import TrafficRoundedIcon from '@mui/icons-material/TrafficRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import AreaChartRoundedIcon from '@mui/icons-material/AreaChartRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import InfoOutlineRoundedIcon from '@mui/icons-material/InfoOutlineRounded';
import ExtensionIcon from '@mui/icons-material/Extension';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import CloudRoundedIcon from '@mui/icons-material/CloudRounded';

// --- TEMPORARY: Coming Soon integrations ---
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
// --- END TEMPORARY ---

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';


const PAGINATION_MENU_ITEMS = [
  { id: 'pagination', title: 'Pagination', icon: <AutoStoriesRoundedIcon />, href: '/dashboard', section: 'Customer section' },
  { id: 'history', title: 'History', icon: <HistoryRoundedIcon />, href: '/history', section: 'Customer section' },
  { id: 'assets', title: 'Asset Management', icon: <PermMediaRoundedIcon />, href: '/assets', section: 'Customer section', authCondition: (user) => user.policy?.canManageAsset || user.isAdmin },
  { id: 'projectinfo', title: 'Project Info', icon: <InfoOutlineRoundedIcon />, href: '/projectinfo', section: 'Customer section' },
  // --- TEMPORARY: Coming Soon integrations ---
  { id: 'integrations', title: 'Integrations', icon: <ExtensionIcon />, href: '/integrations', section: 'Customer section' },
  // --- END TEMPORARY ---
  { id: 'geoxrules', title: 'Regole', icon: <ReceiptLongRoundedIcon />, href: '/geoxrules', section: 'Customer section', authCondition: (user) => user.policy?.canManageGeoxRules || user.isAdmin },

  { id: 'traffic', title: 'Traffic', icon: <TrafficRoundedIcon />, href: '/traffic', section: 'Administrator section', authCondition: (user) => user.isAdmin },
  { id: 'administration', title: 'Administration', icon: <AdminPanelSettingsRoundedIcon />, href: '/administration', section: 'Administrator section', authCondition: (user) => user.isAdmin },

  { id: 'charts', title: 'Charts', icon: <TimelineRoundedIcon />, href: '/charts', section: 'Billing section', authCondition: (user) => user.isAdmin },
  { id: 'billing', title: 'Billing', icon: <AreaChartRoundedIcon />, href: '/billing', section: 'Billing section', authCondition: (user) => user.type === 'billing' || user.isAdmin },
  { id: 'stats', title: 'Stats', icon: <QueryStatsRoundedIcon />, href: '/stats', section: 'Billing section', authCondition: (user) => user.type === 'billing' || user.isAdmin },
];

const LABELS_MENU_ITEMS = [
  { id: 'labels', title: 'Labels', icon: <CloudRoundedIcon />, href: '/labels', section: 'Customer section' },
  { id: 'labels-history', title: 'History', icon: <HistoryRoundedIcon />, href: '/labels/history', section: 'Customer section' },
  { id: 'labels-apikey', title: 'API Key', icon: <VpnKeyRoundedIcon />, href: '/labels/apikey', section: 'Customer section' },

  { id: 'traffic', title: 'Traffic', icon: <TrafficRoundedIcon />, href: '/traffic', section: 'Administrator section', authCondition: (user) => user.isAdmin },
  { id: 'administration', title: 'Administration', icon: <AdminPanelSettingsRoundedIcon />, href: '/administration', section: 'Administrator section', authCondition: (user) => user.isAdmin },

  { id: 'charts', title: 'Charts', icon: <TimelineRoundedIcon />, href: '/charts', section: 'Billing section', authCondition: (user) => user.isAdmin },
  { id: 'billing', title: 'Billing', icon: <AreaChartRoundedIcon />, href: '/billing', section: 'Billing section', authCondition: (user) => user.type === 'billing' || user.isAdmin },
  { id: 'stats', title: 'Stats', icon: <QueryStatsRoundedIcon />, href: '/stats', section: 'Billing section', authCondition: (user) => user.type === 'billing' || user.isAdmin },
];


function DashboardSidebar({
  auth,
  expanded = true,
  setExpanded,
  disableCollapsibleSidebar = false,
  container,
}) {
  const theme = useTheme();
  const history = useHistory();

  const { pathname } = useLocation();
  const { activeService, setActiveService } = React.useContext(ServiceContext);

  const [expandedItemIds, setExpandedItemIds] = React.useState([]);

  const isOverSmViewport = useMediaQuery(theme.breakpoints.up('sm'));
  const isOverMdViewport = useMediaQuery(theme.breakpoints.up('md'));

  const [isFullyExpanded, setIsFullyExpanded] = React.useState(expanded);
  const [isFullyCollapsed, setIsFullyCollapsed] = React.useState(!expanded);

  const canSeeLabels = React.useMemo(() => {
    if (!auth?.userState?.isAuthenticated) return false;
    const user = auth.userState;
    if (user.isAdmin) return true;
    return user.policy?.canManageLabels && user.attached_labels_projects && user.attached_labels_projects.length > 0;
  }, [auth]);

  const handleServiceSwitch = React.useCallback((event, newService) => {
    if (newService === null) return;
    setActiveService(newService);
    if (newService === 'labels') {
      history.push('/labels');
    } else {
      history.push('/dashboard');
    }
  }, [setActiveService, history]);

  React.useEffect(() => {
    if (expanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsFullyExpanded(true);
      }, theme.transitions.duration.enteringScreen);

      return () => clearTimeout(drawerWidthTransitionTimeout);
    }

    setIsFullyExpanded(false);

    return () => { };
  }, [expanded, theme.transitions.duration.enteringScreen]);

  React.useEffect(() => {
    if (!expanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsFullyCollapsed(true);
      }, theme.transitions.duration.leavingScreen);

      return () => clearTimeout(drawerWidthTransitionTimeout);
    }

    setIsFullyCollapsed(false);

    return () => { };
  }, [expanded, theme.transitions.duration.leavingScreen]);

  const mini = !disableCollapsibleSidebar && !expanded;

  const handleSetSidebarExpanded = React.useCallback(
    (newExpanded) => () => {
      setExpanded(newExpanded);
    },
    [setExpanded],
  );

  const handlePageItemClick = React.useCallback(
    (itemId, hasNestedNavigation) => {
      if (hasNestedNavigation && !mini) {
        setExpandedItemIds((previousValue) =>
          previousValue.includes(itemId)
            ? previousValue.filter(
              (previousValueItemId) => previousValueItemId !== itemId,
            )
            : [...previousValue, itemId],
        );
      } else if (!isOverSmViewport && !hasNestedNavigation) {
        setExpanded(false);
      }
    },
    [mini, setExpanded, isOverSmViewport],
  );

  const hasDrawerTransitions = isOverSmViewport && (!disableCollapsibleSidebar || isOverMdViewport);

  // Filter menu items based on auth and active service
  const visibleMenuItems = React.useMemo(() => {
    if (!auth?.userState?.isAuthenticated) return [];
    const user = auth.userState;
    const menuSource = activeService === 'labels' ? LABELS_MENU_ITEMS : PAGINATION_MENU_ITEMS;
    const items = menuSource.filter(item => {
      if (!item.authCondition) return true;
      return item.authCondition(user);
    });
    return items;
  }, [auth, activeService]);

  // Group by sections
  const sections = React.useMemo(() => {
    const result = {};
    visibleMenuItems.forEach(item => {
      if (!result[item.section]) result[item.section] = [];
      result[item.section].push(item);
    });
    return result;
  }, [visibleMenuItems]);



  // --- TEMPORARY: Coming Soon integrations ---
  const canAccessIntegrations = React.useMemo(() => {
    if (!auth?.userState?.isAuthenticated) return false;
    return auth.userState.policy?.canManageIntegrations || auth.userState.isAdmin;
  }, [auth]);

  const [disabledDialogOpen, setDisabledDialogOpen] = React.useState(null);
  // --- END TEMPORARY ---


  const serviceColor = SERVICE_COLORS[activeService] || SERVICE_COLORS.pagination;

  const getDrawerContent = React.useCallback(
    (viewport) => (
      <React.Fragment>
        <Toolbar />
        <Box
          component="nav"
          aria-label={`${viewport.charAt(0).toUpperCase()}${viewport.slice(1)}`}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            overflowX: 'hidden',
            pt: !mini ? 0 : 2,
            ...(hasDrawerTransitions ? getDrawerSxTransitionMixin(isFullyExpanded, 'padding') : {}),
          }}
        >
          {/* Service Switch */}
          {canSeeLabels && !mini && (
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
              <ToggleButtonGroup
                value={activeService}
                exclusive
                onChange={handleServiceSwitch}
                size="small"
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    py: 0.4,
                    borderColor: serviceColor.main,
                    color: serviceColor.main,
                    '&.Mui-selected': {
                      backgroundColor: serviceColor.main,
                      color: serviceColor.contrastText,
                      '&:hover': {
                        backgroundColor: serviceColor.dark,
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="pagination">Pagination</ToggleButton>
                <ToggleButton value="labels">Labels</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <List
            dense
            sx={{
              padding: mini ? 0 : 0.5,
              mb: 4,
              width: mini ? MINI_DRAWER_WIDTH : 'auto',
              mt: 0
            }}
          >
            {Object.entries(sections).map(([sectionName, items]) => (
              <React.Fragment key={sectionName}>
                <DashboardSidebarHeaderItem>{sectionName}</DashboardSidebarHeaderItem>
                {/* {items.map(item => (
                  <DashboardSidebarPageItem
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    icon={item.icon}
                    href={item.href}
                    selected={!!matchPath(item.href, pathname) || pathname === '/'}
                  />
                ))} */}
                {/* --- TEMPORARY: Coming Soon integrations --- (see next)*/}
                {items.map(item => {
                  const isDisabled = (item.id === 'integrations' && !canAccessIntegrations) ||
                    (typeof item.disabledCondition === 'function' && item.disabledCondition(auth.userState));
                  return (
                    <React.Fragment key={item.id}>
                      <Stack direction={'row'} alignItems="center">
                        <DashboardSidebarPageItem
                          id={item.id}
                          title={item.title}
                          icon={item.icon}
                          href={item.href}
                          selected={!isDisabled && (!!matchPath(pathname, { path: item.href, exact: true }) || (pathname === '/' && item.href === '/dashboard'))}
                          disabled={isDisabled}
                        />
                        {isDisabled && !mini && (
                          <IconButton aria-label="info"
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => setDisabledDialogOpen(item.id)}
                            sx={{ cursor: 'pointer', ml: 3, fontSize: '0.65rem', height: 20 }}
                          >
                            <NewReleasesOutlinedIcon />
                          </IconButton>
                        )
                        }
                      </Stack>
                    </React.Fragment>
                  );
                })}
                {/* --- END TEMPORARY: Coming Soon integrations --- (see next)*/}
                <DashboardSidebarDividerItem />
              </React.Fragment>
            ))}
          </List>
        </Box>
      </React.Fragment >
    ),
    [mini, hasDrawerTransitions, isFullyExpanded, sections, pathname, canAccessIntegrations, canSeeLabels, activeService, serviceColor, handleServiceSwitch],
  );



  const getDrawerSharedSx = React.useCallback(
    (isTemporary) => {
      const drawerWidth = mini ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

      return {
        displayPrint: 'none',
        width: drawerWidth,
        flexShrink: 0,
        ...getDrawerWidthTransitionMixin(expanded),
        ...(isTemporary ? { position: 'absolute' } : {}),
        [`& .MuiDrawer-paper`]: {
          position: 'absolute',
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundImage: 'none',
          ...getDrawerWidthTransitionMixin(expanded),
        },
      };
    },
    [expanded, mini],
  );

  const sidebarContextValue = React.useMemo(() => {
    return {
      onPageItemClick: handlePageItemClick,
      mini,
      fullyExpanded: isFullyExpanded,
      fullyCollapsed: isFullyCollapsed,
      hasDrawerTransitions,
    };
  }, [
    handlePageItemClick,
    mini,
    isFullyExpanded,
    isFullyCollapsed,
    hasDrawerTransitions,
  ]);


  return (
    <DashboardSidebarContext.Provider value={sidebarContextValue}>
      <Drawer
        container={container}
        variant="temporary"
        open={expanded}
        onClose={handleSetSidebarExpanded(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: {
            xs: 'block',
            sm: disableCollapsibleSidebar ? 'block' : 'none',
            md: 'none',
          },
          ...getDrawerSharedSx(true),
        }}
      >
        {getDrawerContent('phone')}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: {
            xs: 'none',
            sm: disableCollapsibleSidebar ? 'none' : 'block',
            md: 'none',
          },
          ...getDrawerSharedSx(false),
        }}
      >
        {getDrawerContent('tablet')}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          ...getDrawerSharedSx(false),
        }}
      >
        {getDrawerContent('desktop')}
      </Drawer>
      {/* --- Disabled item dialog --- */}
      <Dialog
        open={!!disabledDialogOpen}
        onClose={() => setDisabledDialogOpen(null)}
        maxWidth="sm"
        fullWidth
      >
        {disabledDialogOpen === 'integrations' && (
          <>
            <DialogTitle>Integrations — Coming Soon!</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                The Integrations page will be available soon! We're building powerful
                tools to connect your favorite services directly with Pagination.
              </DialogContentText>
              <DialogContentText sx={{ mb: 2 }}>
                We'd love to hear from you — share your feedback, expectations, or
                let us know which integrations you'd like to see.
              </DialogContentText>
              <DialogContentText>
                Get in touch at{' '}
                <Link href="mailto:sales@pagination.com" underline="hover">
                  sales@pagination.com
                </Link>
              </DialogContentText>
            </DialogContent>
          </>
        )}
        {disabledDialogOpen === 'labels' && (
          <>
            <DialogTitle>Labels — Access Restricted</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                The Labels service is not enabled for your account. Contact your
                administrator to request access.
              </DialogContentText>
              <DialogContentText>
                Get in touch at{' '}
                <Link href="mailto:sales@pagination.com" underline="hover">
                  sales@pagination.com
                </Link>
              </DialogContentText>
            </DialogContent>
          </>
        )}
        <DialogActions>
          <Button onClick={() => setDisabledDialogOpen(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* --- END Disabled item dialog --- */}
    </DashboardSidebarContext.Provider>
  );
}

DashboardSidebar.propTypes = {
  container: (props, propName) => {
    if (props[propName] == null) {
      return null;
    }
    if (typeof props[propName] !== 'object' || props[propName].nodeType !== 1) {
      return new Error(`Expected prop '${propName}' to be of type Element`);
    }
    return null;
  },
  disableCollapsibleSidebar: PropTypes.bool,
  expanded: PropTypes.bool,
  setExpanded: PropTypes.func.isRequired,
};

export default DashboardSidebar;
